<template>
  <div class="bulk">
    <q-inner-loading :showing="this.loading" color="primary" label="Назначение…" label-class="text-primary" />

    <div :class="['bulk__content', { 'bulk__content--blocked': this.loading }]">
    <div class="bulk__steps">
      <div :class="['bulk__step', { 'bulk__step--active': this.step === 1, 'bulk__step--done': this.step > 1 }]">
        1. Проектировщик
      </div>
      <div class="bulk__stepsDivider" />
      <div :class="['bulk__step', { 'bulk__step--active': this.step === 2 }]">
        2. Ремонты
      </div>
    </div>

    <div v-if="this.sessionAssignedCount" class="bulk__sessionBar">
      В этой сессии назначено: <b>{{ this.sessionAssignedCount }}</b>
      · осталось: <b>{{ this.availableRemonts.length }}</b>
    </div>

    <!-- Step 1: choose designer -->
    <div v-if="this.step === 1" class="bulk__form">
      <div class="bulk__section">
        <div class="bulk__sectionTitle">Выберите проектировщика</div>
        <q-input
          v-model="this.employeeSearch"
          outlined
          dense
          clearable
          autofocus
          :disable="this.loading"
          placeholder="Поиск проектировщика…"
          class="bulk__search"
        >
          <template #prepend>
            <q-icon name="search" size="18px" />
          </template>
        </q-input>
        <div class="bulk__employeeList bulk__employeeList--step1">
          <div
            v-for="emp in this.filteredEmployees"
            :key="emp.employee_id"
            :class="[
              'bulk__employeeItem',
              { 'bulk__employeeItem--selected': emp.employee_id === this.techproject_employee_id },
            ]"
            @click="!this.loading && this.selectEmployee(emp.employee_id)"
          >
            <span>{{ emp.fio || emp.techproject_name || emp.name }}</span>
            <q-icon
              v-if="emp.employee_id === this.techproject_employee_id"
              name="check"
              size="16px"
              color="primary"
            />
          </div>
          <div v-if="!this.filteredEmployees.length" class="bulk__emptyInline">
            Не найдено
          </div>
        </div>
      </div>

      <div v-if="this.lastResult" class="bulk__result">
        <div class="bulk__resultOk">
          Последнее назначение: {{ this.lastResult.success_count }}
          <template v-if="this.lastResult.designer"> → {{ this.lastResult.designer }}</template>
        </div>
        <div v-if="this.lastResult.error_count" class="bulk__resultErr">
          Ошибок: {{ this.lastResult.error_count }}
        </div>
      </div>

      <div class="bulk__footer">
        <q-btn
          class="bulk__btn bulk__btn--cancel"
          :label="this.sessionAssignedCount ? 'Закрыть' : 'Отмена'"
          no-caps
          :disable="this.loading"
          @click="this.handleClose"
        />
        <q-btn
          class="bulk__btn bulk__btn--submit"
          label="Далее"
          no-caps
          :disable="this.loading || !this.techproject_employee_id || !this.availableRemonts.length"
          @click="this.goToRemonts"
        />
      </div>
    </div>

    <!-- Step 2: choose remonts -->
    <q-form v-else @submit.prevent="this.onSubmit" class="bulk__form">
      <div class="bulk__selectedBar">
        <div class="bulk__selectedBarText">
          Проектировщик: <b>{{ this.selectedEmployeeLabel }}</b>
        </div>
        <q-btn
          flat
          dense
          no-caps
          color="primary"
          label="Изменить"
          class="bulk__changeBtn"
          :disable="this.loading"
          @click="this.backToEmployee"
        />
      </div>

      <div class="bulk__section">
        <div class="bulk__sectionHeader">
          <div class="bulk__sectionTitle">Выберите ремонты</div>
          <span class="bulk__counter">
            выбрано {{ this.selectedIds.length }} / {{ this.availableRemonts.length }}
          </span>
        </div>

        <q-input
          v-model="this.search"
          outlined
          dense
          clearable
          autofocus
          :disable="this.loading"
          placeholder="Поиск: ID ремонта, заявка, ЖК, квартира…"
          class="bulk__search"
        >
          <template #prepend>
            <q-icon name="search" size="18px" />
          </template>
        </q-input>

        <div class="bulk__toolbar">
          <CustomCheckbox
            :value="this.allFilteredSelected"
            label="Выбрать все на экране"
            @change="this.toggleAllFiltered"
          />
          <span class="bulk__shown">показано {{ this.filteredRemonts.length }}</span>
        </div>

        <div class="bulk__list" v-if="this.filteredRemonts.length">
          <div
            v-for="row in this.filteredRemonts"
            :key="row.remont_id"
            :class="[
              'bulk__row',
              {
                'bulk__row--selected': this.isSelected(row.remont_id),
                'bulk__row--error': this.errorIds.includes(row.remont_id),
              },
            ]"
          >
            <CustomCheckbox
              :value="this.isSelected(row.remont_id)"
              @change="(v) => this.toggleRemont(row.remont_id, v)"
            />
            <div class="bulk__rowBody">
              <div class="bulk__rowTop">
                <Clickable
                  router-link
                  :href="`/${PAGES_HEADS.remont}/${row.remont_id}`"
                  class="bulk__rowId"
                >
                  Ремонт #{{ row.remont_id }}
                </Clickable>
                <span v-if="row.client_request_id" class="bulk__rowMeta">
                  заявка {{ row.client_request_id }}
                </span>
                <span v-if="this.errorById[row.remont_id]" class="bulk__rowErrBadge">
                  ошибка
                </span>
              </div>
              <div class="bulk__rowAddr">
                <template v-if="row.resident_name">{{ row.resident_name }}</template>
                <template v-if="row.flat_num">
                  <template v-if="row.resident_name">, </template>
                  кв. {{ row.flat_num }}
                </template>
                <template v-if="!row.resident_name && !row.flat_num">—</template>
              </div>
              <div v-if="this.errorById[row.remont_id]" class="bulk__rowErrMsg">
                {{ this.errorById[row.remont_id] }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="bulk__empty">
          {{ this.availableRemonts.length ? 'Нет ремонтов по фильтру' : 'Все ремонты назначены' }}
        </div>
      </div>

      <div class="bulk__footer">
        <q-btn
          class="bulk__btn bulk__btn--cancel"
          label="Назад"
          no-caps
          :disable="this.loading"
          @click="this.backToEmployee"
        />
        <q-btn
          class="bulk__btn bulk__btn--submit"
          :label="this.submitLabel"
          type="submit"
          no-caps
          :disable="!this.canSubmit"
          :loading="this.loading"
        />
      </div>
    </q-form>
    </div>
  </div>
</template>

<script>
import CustomCheckbox from "@/components/form/CustomCheckbox";
import Clickable from "@/components/common/Clickable";
import {setExecutiveEmployeeBulk} from "@/views/ExecutiveReport/services";
import {useCustomSnackbar} from "@/_helpers/hooks/useCustomSnackbar";
import {PAGES_HEADS} from "@/constants";

export default {
  name: "executive-report-bulk-assign-designer",
  components: {Clickable, CustomCheckbox},
  props: ['data', 'closeModal'],
  data() {
    return {
      PAGES_HEADS,
      step: 1,
      loading: false,
      search: '',
      employeeSearch: '',
      techproject_employee_id: '',
      selectedIds: [],
      availableRemonts: [...(this.data?.remonts || [])],
      sessionAssignedCount: 0,
      lastResult: null,
      errorById: {},
    }
  },
  computed: {
    employees() {
      return this.data?.employees || []
    },
    errorIds() {
      return Object.keys(this.errorById).map(Number)
    },
    filteredEmployees() {
      const q = (this.employeeSearch || '').trim().toLowerCase()
      if (!q) return this.employees
      return this.employees.filter((emp) => {
        const label = emp.fio || emp.techproject_name || emp.name || ''
        return String(label).toLowerCase().includes(q)
      })
    },
    selectedEmployeeLabel() {
      const emp = this.employees.find((e) => e.employee_id === this.techproject_employee_id)
      if (!emp) return ''
      return emp.fio || emp.techproject_name || emp.name || ''
    },
    filteredRemonts() {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return this.availableRemonts
      return this.availableRemonts.filter((row) => {
        return (
          String(row.remont_id || '').includes(q) ||
          String(row.client_request_id || '').includes(q) ||
          String(row.resident_name || '').toLowerCase().includes(q) ||
          String(row.flat_num || '').toLowerCase().includes(q)
        )
      })
    },
    filteredIds() {
      return this.filteredRemonts.map((r) => r.remont_id)
    },
    allFilteredSelected() {
      return this.filteredIds.length > 0 &&
        this.filteredIds.every((id) => this.selectedIds.includes(id))
    },
    canSubmit() {
      return !!this.techproject_employee_id && this.selectedIds.length > 0 && !this.loading
    },
    submitLabel() {
      if (!this.selectedIds.length) return 'Выберите ремонты'
      return `Назначить ${this.selectedIds.length} ремонтов`
    },
  },
  methods: {
    setLoading(val) {
      this.loading = val
      const data = this.$store.state.app.modal?.data
      if (data) data.preventClose = !!val
    },
    handleClose() {
      if (this.loading) return
      this.closeModal()
    },
    selectEmployee(employee_id) {
      if (this.loading) return
      this.techproject_employee_id = employee_id
    },
    goToRemonts() {
      if (this.loading || !this.techproject_employee_id || !this.availableRemonts.length) return
      this.step = 2
      this.selectedIds = []
      this.errorById = {}
    },
    backToEmployee() {
      if (this.loading) return
      this.step = 1
      this.selectedIds = []
      this.errorById = {}
    },
    isSelected(remont_id) {
      return this.selectedIds.includes(remont_id)
    },
    toggleRemont(remont_id, checked) {
      if (this.loading) return
      if (checked) {
        if (!this.selectedIds.includes(remont_id)) {
          this.selectedIds = [...this.selectedIds, remont_id]
        }
      } else {
        this.selectedIds = this.selectedIds.filter((id) => id !== remont_id)
      }
    },
    toggleAllFiltered(checked) {
      if (this.loading) return
      if (checked) {
        const set = new Set([...this.selectedIds, ...this.filteredIds])
        this.selectedIds = Array.from(set)
      } else {
        this.selectedIds = this.selectedIds.filter((id) => !this.filteredIds.includes(id))
      }
    },
    async onSubmit() {
      if (!this.canSubmit) return
      this.setLoading(true)
      const designer = this.selectedEmployeeLabel
      try {
        const res = await setExecutiveEmployeeBulk({
          techproject_employee_id: this.techproject_employee_id,
          remont_ids: this.selectedIds,
        })
        if (!res) return

        const successIds = new Set(res.success_remont_ids || [])
        const nextErrors = {}
        ;(res.errors || []).forEach((err) => {
          nextErrors[err.remont_id] = err.message
        })

        this.availableRemonts = this.availableRemonts.filter(
          (row) => !successIds.has(row.remont_id)
        )
        this.selectedIds = this.selectedIds.filter((id) => !successIds.has(id))
        this.errorById = nextErrors
        this.sessionAssignedCount += res.success_count || 0
        this.lastResult = {
          success_count: res.success_count || 0,
          error_count: res.error_count || 0,
          designer,
        }

        if (res.error_count) {
          this.showWarningSnackbar(
            `Назначено ${res.success_count}, ошибок: ${res.error_count}. Ошибочные остались в списке.`
          )
          return
        }

        this.showSucceedSnackbar(`Назначено ремонтов: ${res.success_count}`)

        if (!this.availableRemonts.length) {
          this.showSucceedSnackbar(
            `Готово. Всего в сессии: ${this.sessionAssignedCount}`
          )
          this.setLoading(false)
          this.closeModal()
          return
        }

        this.techproject_employee_id = ''
        this.employeeSearch = ''
        this.selectedIds = []
        this.errorById = {}
        this.step = 1
      } finally {
        this.setLoading(false)
      }
    },
  },
  setup() {
    const {showSucceedSnackbar, showWarningSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar, showWarningSnackbar}
  },
}
</script>

<style scoped lang="scss">
.bulk {
  position: relative;
  width: min(860px, 92vw);
  max-width: 860px;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
  }

  &__content {
    &--blocked {
      pointer-events: none;
      user-select: none;
      opacity: 0.72;
    }
  }

  &__steps {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__step {
    font-size: 12px;
    font-weight: 600;
    color: var(--ms-muted);
    padding: 4px 0;

    &--active {
      color: var(--ms-primary);
    }

    &--done {
      color: var(--ms-success);
    }
  }

  &__stepsDivider {
    flex: 1;
    height: 1px;
    background: var(--ms-border);
  }

  &__sessionBar {
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #eef8ee;
    border: 1px solid #cfe8cf;
    border-radius: var(--ms-radius-sm);
    font-size: 12px;
    color: #2d5a2d;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__sectionHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__sectionTitle {
    font-size: 13px;
    font-weight: 600;
    color: var(--ms-text);
  }

  &__counter,
  &__shown {
    font-size: 12px;
    color: var(--ms-muted);
  }

  &__search {
    width: 100%;
  }

  &__selectedBar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    background: var(--ms-primary-50);
    border: 1px solid var(--ms-primary-100);
    border-radius: var(--ms-radius-sm);
  }

  &__selectedBarText {
    font-size: 13px;
    color: var(--ms-text);
  }

  &__changeBtn {
    font-size: 12px;
  }

  &__employeeList {
    max-height: 180px;
    overflow: auto;
    border: 1px solid var(--ms-border);
    border-radius: var(--ms-radius-sm);

    &--step1 {
      max-height: min(420px, 52vh);
    }
  }

  &__employeeItem {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    font-size: 13px;
    border-bottom: 1px solid var(--ms-divider);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--ms-row-hover);
    }

    &--selected {
      background: var(--ms-primary-50);
      font-weight: 600;
      color: var(--ms-primary);
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__list {
    max-height: min(420px, 48vh);
    overflow: auto;
    border: 1px solid var(--ms-border);
    border-radius: var(--ms-radius-sm);
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--ms-divider);

    &:last-child {
      border-bottom: none;
    }

    &--selected {
      background: var(--ms-primary-50);
    }

    &--error {
      background: #fff5f5;
    }
  }

  &__rowBody {
    flex: 1;
    min-width: 0;
  }

  &__rowTop {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__rowId {
    font-size: 13px;
    font-weight: 600;
  }

  &__rowMeta {
    font-size: 12px;
    color: var(--ms-muted);
  }

  &__rowErrBadge {
    font-size: 11px;
    color: var(--ms-danger);
    font-weight: 600;
  }

  &__rowAddr {
    font-size: 12px;
    color: var(--ms-text-secondary);
    margin-top: 2px;
  }

  &__rowErrMsg {
    margin-top: 4px;
    font-size: 11px;
    color: var(--ms-danger);
  }

  &__empty,
  &__emptyInline {
    padding: 16px;
    text-align: center;
    color: var(--ms-muted);
    font-size: 13px;
  }

  &__empty {
    border: 1px dashed var(--ms-border-input);
    border-radius: var(--ms-radius-sm);
  }

  &__result {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
  }

  &__resultOk {
    color: var(--ms-success);
  }

  &__resultErr {
    color: var(--ms-danger);
  }

  &__footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-top: 4px;
  }

  &__btn {
    font-size: 14px;
    border-radius: 8px;
    min-height: 40px;

    &--cancel {
      background: #fff;
      color: var(--ms-primary);
      border: 1px solid var(--ms-border-input);
    }

    &--submit {
      background: var(--ms-primary);
      color: #fff;

      &:disabled {
        opacity: 0.55;
      }
    }
  }
}
</style>
